import React, { useEffect, useMemo } from "react";
import { X, Check, Minus } from "lucide-react";
import { brand as ds } from "../designSystem";
import { CONTRACT_TERMS, trialTerms, TRIAL_TERMINATION } from "../plans";

/* ════════════════════════════════════════════════════════════════════════
   CHECKOUT CONSENT — the last screen before Stripe.
   ────────────────────────────────────────────────────────────────────────
   This replaces a browser `window.confirm()` that dumped two paragraphs of
   BGB prose into an OS dialog. That dialog was bad law and bad product: an
   OS alert is not a documented declaration, "OK" is not an unambiguous
   act of consent, and it read like a warning at the exact moment a
   customer decides whether to trust you.

   What this screen has to do, and does:

   § 312j Abs. 2 BGB — the essential characteristics, the total price, the
     contract duration and the termination conditions appear directly above
     the order button, not in a footer and not behind a link.
   § 312j Abs. 3 BGB — the label "Zahlungspflichtig bestellen" belongs on
     the button that actually triggers the binding order. With Stripe
     Checkout that is Stripe's own pay button, which reads "Kostenpflichtig
     abonnieren" (checked on the live checkout, November 2026). This
     screen's button only forwards, so it says "Weiter zur Zahlung". AGB § 3
     names Stripe's label — if Stripe ever changes it, change it there too.
   § 305 Abs. 2 BGB — the terms are incorporated by a clear notice with a
     link the customer can actually open (new tab, so the checkout is not
     lost). That is all the law asks; a checkbox is not required. The
     privacy policy is information, not something to "agree to": the data
     processing for an order rests on the contract (Art. 6 Abs. 1 lit. b
     DSGVO), not on consent.
   Widerruf — there is deliberately NO waiver checkbox (§ 356 Abs. 4/5
     BGB). For a monthly subscription it would rarely take effect anyway,
     because the right only lapses once the service is fully performed.
     Customers keep the statutory 14-day withdrawal right; the notice below
     links the Widerrufsbelehrung, which has to be provided either way.

   The exact wording shown is versioned (CONSENT_VERSION) and sent to the
   backend with the order, so what someone was shown stays reconstructable.
   ════════════════════════════════════════════════════════════════════════ */

// 2026-10-29: Kündigung in der Testphase beendet sie sofort.
// 2026-11-02: Hinweis auf das gesetzliche Gewährleistungsrecht (Art. 246a
// § 1 Abs. 1 Nr. 11b EGBGB), AGB neu gefasst.
export const CONSENT_VERSION = "2026-11-02";

export const CONSENT_TEXTS = {
  terms:
    "Es gelten die AGB. Informationen zum Datenschutz stehen in der Datenschutzerklärung. Du hast ein gesetzliches Widerrufsrecht von 14 Tagen (Widerrufsbelehrung). Es gilt das gesetzliche Gewährleistungsrecht für digitale Dienstleistungen.",
};

export default function CheckoutConsent({ plan, onConfirm, onClose, busy }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const ready = !busy;
  const isTrial = Boolean(plan?.trialDays);

  const styles = useMemo(
    () => ({
      scrim: {
        position: "fixed",
        inset: 0,
        zIndex: 300,
        background: ds.scrim,
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        overflowY: "auto",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
      },
      card: {
        width: "100%",
        maxWidth: 560,
        background: "rgba(247,247,248,0.92)",
        backdropFilter: "blur(24px) saturate(180%)",
        WebkitBackdropFilter: "blur(24px) saturate(180%)",
        border: "1px solid rgba(11,12,15,0.08)",
        borderRadius: ds.radius.lg,
        marginTop: "auto",
        marginBottom: "auto",
      },
      title: {
        fontFamily: ds.font.display,
        fontWeight: 400,
        fontSize: ds.text.h2,
        lineHeight: ds.leading.tight,
        letterSpacing: ds.tracking.tight,
        color: ds.color.onLight,
        margin: 0,
      },
      well: {
        background: "rgba(11,12,15,0.04)",
        borderRadius: ds.radius.md,
        padding: 20,
      },
      label: {
        fontFamily: ds.font.body,
        fontSize: 14,
        fontWeight: 600,
        lineHeight: 1.5,
        color: ds.color.onLightMuted,
        margin: 0,
      },
      body: {
        fontFamily: ds.font.body,
        fontSize: 16,
        fontWeight: 400,
        lineHeight: 1.6,
        color: ds.color.onLight,
        margin: 0,
      },
      fine: {
        fontFamily: ds.font.body,
        fontSize: 14,
        fontWeight: 400,
        lineHeight: 1.55,
        color: ds.color.onLightMuted,
        margin: 0,
      },
    }),
    []
  );

  if (!plan) return null;

  return (
    <div
      className="sb-consent-scrim"
      style={styles.scrim}
      onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label="Bestellung abschließen"
    >
      <style>{`
        .sb-consent-scrim { padding: 24px 16px 48px; }
        .sb-consent-card { padding: 32px; }
        @media (max-width: 480px) {
          .sb-consent-scrim { padding: 16px 6px 32px; }
          .sb-consent-card { padding: 22px 14px; }
        }
      `}</style>
      <div
        className="sb-consent-card"
        style={styles.card}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            marginBottom: 24,
          }}
        >
          <h2 style={styles.title}>Bestellung prüfen</h2>
          <button
            onClick={onClose}
            disabled={busy}
            aria-label="Schließen"
            style={{
              flex: "none",
              width: 38,
              height: 38,
              borderRadius: ds.radius.pill,
              border: "none",
              background: "rgba(11,12,15,0.06)",
              color: ds.color.onLightMuted,
              cursor: busy ? "not-allowed" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* ── § 312j Abs. 2 BGB — what is being ordered, at what price ── */}
        <div style={{ ...styles.well, marginBottom: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: 16,
              marginBottom: 4,
            }}
          >
            <span
              style={{
                fontFamily: ds.font.display,
                fontSize: ds.text.h3,
                fontWeight: 400,
                color: ds.color.onLight,
              }}
            >
              Schriftbot {plan.name}
            </span>
            <span
              style={{
                fontFamily: ds.font.body,
                fontSize: 20,
                fontWeight: 600,
                color: ds.color.onLight,
                whiteSpace: "nowrap",
              }}
            >
              {isTrial ? `${plan.trialDays} Tage gratis` : plan.price}
              {!isTrial && (
                <span style={{ color: ds.color.onLightMuted, fontWeight: 400 }}>
                  {" "}
                  / {CONTRACT_TERMS.interval}
                </span>
              )}
            </span>
          </div>
          {/* Der Preis danach gehört genauso sichtbar dazu (§ 312j Abs. 2). */}
          {isTrial && (
            <p style={{ ...styles.fine, textAlign: "right" }}>
              danach {plan.price} / {CONTRACT_TERMS.interval}
            </p>
          )}

          <ul
            style={{
              listStyle: "none",
              margin: "16px 0 0",
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {plan.features.map((f) => (
              <li
                key={f.label}
                style={{
                  ...styles.fine,
                  color: f.on ? ds.color.onLight : "rgba(11,12,15,0.35)",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                {f.on ? (
                  <Check size={16} style={{ flex: "none" }} />
                ) : (
                  <Minus size={16} style={{ flex: "none" }} />
                )}
                {f.label}
              </li>
            ))}
          </ul>
        </div>

        {/* Duration + termination + VAT — required directly above the button.
            Ein Fließtext statt drei gestapelter Absätze: derselbe Pflichttext,
            aber er liest sich nicht wie ein Wall of Text. */}
        <div style={{ ...styles.well, marginBottom: 20 }}>
          <p style={styles.fine}>
            {isTrial ? trialTerms(plan) : CONTRACT_TERMS.duration}{" "}
            {isTrial ? TRIAL_TERMINATION : CONTRACT_TERMS.termination}{" "}
            {CONTRACT_TERMS.vat}
          </p>
        </div>

        {/* ── Hinweis statt Häkchen (siehe Kopfkommentar) ── */}
        <p style={{ ...styles.fine, marginBottom: 20 }}>
          Es gelten unsere{" "}
          <a
            href="/agb"
            target="_blank"
            rel="noopener noreferrer"
            className="sb-link"
            style={{ color: ds.color.onLight }}
          >
            AGB
          </a>
          . Informationen zum Datenschutz stehen in der{" "}
          <a
            href="/datenschutz"
            target="_blank"
            rel="noopener noreferrer"
            className="sb-link"
            style={{ color: ds.color.onLight }}
          >
            Datenschutzerklärung
          </a>
          . Du hast ein gesetzliches Widerrufsrecht von 14 Tagen (
          <a
            href="/agb#widerruf"
            target="_blank"
            rel="noopener noreferrer"
            className="sb-link"
            style={{ color: ds.color.onLight }}
          >
            Widerrufsbelehrung
          </a>
          ). Es gilt das gesetzliche Gewährleistungsrecht für digitale
          Dienstleistungen.
        </p>

        {/* ── Der Weiter-Schritt ──
            Die Pflichtbeschriftung nach § 312j Abs. 3 BGB sitzt auf der
            Schaltfläche, die die Bestellung tatsächlich auslöst — und das
            ist die Bezahl-Schaltfläche in Stripe Checkout, nicht diese
            hier. Diese Schaltfläche führt nur dorthin, deshalb benennt sie
            genau das. Siehe die Notiz oben zum Nachprüfen. ── */}
        <button
          onClick={() =>
            onConfirm({
              consent: {
                version: CONSENT_VERSION,
                terms: true,
                textTerms: CONSENT_TEXTS.terms,
                at: new Date().toISOString(),
              },
            })
          }
          disabled={!ready}
          className="sb-btn sb-btn-primary"
          style={{ width: "100%" }}
        >
          {busy
            ? "Einen Moment …"
            : isTrial
            ? "Weiter zur Bezahlseite"
            : "Weiter zur Zahlung"}
        </button>

        <p
          style={{
            ...styles.fine,
            textAlign: "center",
            marginTop: 14,
          }}
        >
          {isTrial
            ? `Heute zahlst du 0 €. Stripe fragt eine Zahlungsart ab und bucht erst nach ${plan.trialDays} Tagen ab.`
            : "Du wirst zur gesicherten Bezahlseite von Stripe weitergeleitet."}{" "}
          Kündigen geht jederzeit.
        </p>
      </div>
    </div>
  );
}
